import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { motion } from 'framer-motion';

interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ReactNode;
  active?: boolean;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  showHome?: boolean;
  className?: string;
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ 
  items, 
  showHome = true,
  className = '' 
}) => {
  const location = useLocation();

  const allItems = showHome 
    ? [{ label: 'Dashboard', href: '/dashboard', icon: <Home size={16} /> }, ...items]
    : items;

  return (
    <nav aria-label="Breadcrumb" className={`mb-4 ${className}`}>
      <motion.ol 
        className="flex items-center space-x-2 text-sm"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {allItems.map((item, index) => {
          const isLast = index === allItems.length - 1;
          const isActive = ('active' in item && item.active) || isLast || location.pathname === item.href;

          return (
            <motion.li 
              key={index} 
              className="flex items-center"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              {index > 0 && (
                <ChevronRight className="mx-2 text-gray-400" size={16} />
              )}
              
              {isActive ? (
                <span className="flex items-center gap-1.5 text-gray-900 font-medium">
                  {item.icon}
                  {item.label}
                </span>
              ) : item.href ? (
                <Link
                  to={item.href}
                  className="flex items-center gap-1.5 text-gray-600 hover:text-blue-600 transition-colors duration-200"
                >
                  {item.icon}
                  {item.label}
                </Link>
              ) : (
                <span className="flex items-center gap-1.5 text-gray-600">
                  {item.icon}
                  {item.label}
                </span>
              )}
            </motion.li>
          );
        })}
      </motion.ol>
    </nav>
  );
};

// Simplified component for inline usage
export const BreadcrumbItem: React.FC<{
  children: React.ReactNode;
  href?: string;
  active?: boolean;
}> = ({ children, href, active }) => {
  if (active || !href) {
    return <span className="text-gray-900 font-medium">{children}</span>;
  }
  
  return (
    <Link to={href} className="text-gray-600 hover:text-blue-600 transition-colors">
      {children}
    </Link>
  );
};